import React from "react";
import Moment from "moment";
import Icon from "components/blocks/Icon/Icon";
import "./SheetLayout.scss";
import { decimalFormatter, moneyFormatter, percentageFormatter } from "@/services/utils/useUtils";

// ── Formatters ────────────────────────────────────────────────────────────────

export const formatText = (value: any): string =>
  value !== null && value !== undefined && String(value).trim() !== ""
    ? String(value)
    : "—";

export const formatDate = (value: any): string => {
  if (!value) return "—";
  const parsed = Moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

export const formatCurrency = (value: any): string => {
  return value !== null && value !== undefined
    ? `${moneyFormatter.format(value)}`
    : "—";
};

export const formatBoolean = (value: any): string =>
  value === true ? "Sí" : value === false ? "No" : "—";

export const formatDecimal = (value: any): string => {
  return value !== null && value !== undefined
    ? `${decimalFormatter.format(value)}`
    : "—";
};

export const formatPercent = (value: any): string => {
  return value !== null && value !== undefined
    ? `${percentageFormatter.format(value)}`
    : "—";
};

// ── EntornoLaboral props builder ──────────────────────────────────────────────

export const buildEntornoLaboralProps = (data: any) => ({
  id: "EntornoLaboral",
  nombre: data?.entornoLaboral?.Nombre ?? "",
  CCC: data?.entornoLaboral?.CCC ?? "",
  CIF: data?.entornoLaboral?.CIF ?? "",
  EntornoLaboralEmpresaId: data?.entornoLaboral?.EntornoLaboralEmpresaId ?? "",
  EsCuentaPropia: data?.entornoLaboral?.EsCuentaPropia ?? false,
  NAF: data?.entornoLaboral?.NAF ?? "",
  data,
});

// ── Page wrapper ──────────────────────────────────────────────────────────────

export const SheetPage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <section className="container">
    <div className="sh-page">{children}</div>
  </section>
);

// ── Hero ──────────────────────────────────────────────────────────────────────

interface SheetHeroProps {
  title: string;
  icon: string;
  pills?: React.ReactNode;
  actions?: React.ReactNode;
}

export const SheetHero: React.FC<SheetHeroProps> = ({
  title,
  icon,
  pills,
  actions,
}) => (
  <div className="sh-hero">
    <div className="sh-hero__icon">
      <Icon name={icon} size={22} />
    </div>
    <div className="sh-hero__body">
      <h2 className="sh-hero__title">{title}</h2>
      {pills && <div className="sh-hero__pills">{pills}</div>}
    </div>
    {actions && <div className="sh-hero__actions">{actions}</div>}
  </div>
);

export const SheetPill: React.FC<{
  label: string;
  value: string;
  icon?: string;
}> = ({ label, value, icon }) => (
  <span className="sh-hero__pill">
    {icon && <Icon name={icon} size={11} />}
    {label}: <strong>{value}</strong>
  </span>
);

// ── Section card ──────────────────────────────────────────────────────────────

interface SheetSectionProps {
  title: string;
  icon?: string;
  children: React.ReactNode;
  alert?: React.ReactNode;
}

export const SheetSection: React.FC<SheetSectionProps> = ({
  title,
  icon = "file-text",
  children,
  alert,
}) => (
  <div className="sh-section">
    {alert && <div className="sh-alert sh-alert--warn">{alert}</div>}
    <div className="sh-section__head">
      <span className="sh-section__head-icon">
        <Icon name={icon} size={14} />
      </span>
      <h3 className="sh-section__title">{title}</h3>
    </div>
    <div className="sh-section__body">{children}</div>
  </div>
);

// ── Fields grid ───────────────────────────────────────────────────────────────

export const SheetGrid: React.FC<{
  children: React.ReactNode;
  cols?: 2 | 3 | 4;
}> = ({ children, cols = 4 }) => (
  <div className={`sh-grid sh-grid--${cols}`}>{children}</div>
);

export const SheetField: React.FC<{
  label: string;
  value: string;
  wide?: boolean;
  half?: boolean;
}> = ({ label, value, wide, half }) => (
  <div
    className={`sh-field${wide ? " sh-field--wide" : ""}${half ? " sh-field--half" : ""}`}
  >
    <span className="sh-field__label">{label}</span>
    <span className="sh-field__value">{value || "—"}</span>
  </div>
);

export const SheetDiagnosisField: React.FC<{
  label: string;
  code: string;
  desc: string;
  wide?: boolean;
}> = ({ label, code, desc, wide }) => (
  <div className={`sh-field${wide ? " sh-field--wide" : ""}`}>
    <span className="sh-field__label">{label}</span>
    <span className="sh-field__value sh-field__value--diag">
      {code && <span className="sh-field__code">{code}</span>}
      <span>{desc || "—"}</span>
    </span>
  </div>
);

export const SheetTextField: React.FC<{
  label: string;
  value: string;
  wide?: boolean;
}> = ({ label, value, wide }) => (
  <div className={`sh-field${wide ? " sh-field--wide" : ""}`}>
    <span className="sh-field__label">{label}</span>
    <p className="sh-text-block" style={{ marginTop: 4 }}>
      {value || "—"}
    </p>
  </div>
);

// ── Highlight cards ───────────────────────────────────────────────────────────

export const SheetHighlightRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="sh-highlight-row">{children}</div>;

export const SheetHighlight: React.FC<{
  label: string;
  value: string;
  variant: "blue" | "green" | "amber";
}> = ({ label, value, variant }) => (
  <div className={`sh-highlight sh-highlight--${variant}`}>
    <span className="sh-highlight__label">{label}</span>
    <span className="sh-highlight__value">{value}</span>
  </div>
);
